/**
 * Secure localStorage cache with TTL support and user-scoped keys
 * Ensures data is isolated per user and validated before use
 */

class SecureCache {
  constructor(prefix = 'theraengage') {
    this.prefix = prefix;
    this.isAvailable = this.checkAvailability();
  }

  /**
   * Check if localStorage is available
   */
  checkAvailability() {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return false;
      }
      // Test write/read
      const testKey = '__cache_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      // localStorage might be disabled (private browsing, etc.)
      return false;
    }
  }

  /**
   * Get cache key - scoped to user for security
   * @param {string} name - Cache entry name
   * @param {string} userId - User ID (netlifyId or userId) for scoping
   */
  getKey(name, userId = null) {
    if (!userId) {
      console.warn('Cache key requested without userId - this may be a security issue');
      return `${this.prefix}_${name}`;
    }
    // Include userId in key to ensure user-specific data isolation
    return `${this.prefix}_${name}_${userId}`;
  }

  /**
   * Get cached data with validation
   * @param {string} name - Cache entry name
   * @param {string} userId - User ID for validation
   * @returns {object|null} - Cached data or null if invalid/expired
   */
  get(name, userId = null) {
    if (!this.isAvailable) {
      return null;
    }

    try {
      if (!userId) {
        console.warn('Cache get called without userId - skipping for security');
        return null;
      }

      const key = this.getKey(name, userId);
      const cached = localStorage.getItem(key);
      
      if (!cached) {
        return null;
      }

      const data = JSON.parse(cached);
      
      // Security: Validate userId matches (prevents cross-user data access)
      if (data.userId && data.userId !== userId) {
        console.warn('Cache userId mismatch - clearing potentially tampered cache');
        this.delete(name, userId);
        return null;
      }

      // Check if expired
      if (data.expiresAt) {
        const expiresAt = new Date(data.expiresAt);
        if (expiresAt < new Date()) {
          this.delete(name, userId);
          return null;
        }
      }

      // Return data without metadata
      const { cachedAt, expiresAt, userId: _userId, ...cleanData } = data;
      return cleanData;
    } catch (error) {
      console.error('Cache get error:', error);
      // If cache is corrupted, delete it
      this.delete(name, userId);
      return null;
    }
  }

  /**
   * Set cache with TTL and user validation
   * @param {string} name - Cache entry name
   * @param {object} data - Data to cache
   * @param {number} ttlMinutes - Time to live in minutes
   * @param {string} userId - User ID for scoping
   * @returns {boolean} - Success status
   */
  set(name, data, ttlMinutes = 60, userId = null) {
    if (!this.isAvailable) {
      return false;
    }

    try {
      if (!userId) {
        console.warn('Cache set called without userId - skipping for security');
        return false;
      }

      const key = this.getKey(name, userId);
      const expiresAt = new Date(Date.now() + ttlMinutes * 60 * 1000);
      
      // Store with metadata for validation
      const cacheData = {
        ...data,
        userId: userId, // Store userId for validation
        cachedAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString()
      };

      localStorage.setItem(key, JSON.stringify(cacheData));
      return true;
    } catch (error) {
      console.error('Cache set error:', error);
      
      // Handle quota exceeded
      if (error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded - clearing expired entries');
        this.clearExpired();
        // Try again
        try {
          localStorage.setItem(key, JSON.stringify(cacheData));
          return true;
        } catch (retryError) {
          console.error('Cache set retry failed:', retryError);
          return false;
        }
      }
      return false;
    }
  }

  /**
   * Delete cache entry
   * @param {string} name - Cache entry name
   * @param {string} userId - User ID
   * @returns {boolean} - Success status
   */
  delete(name, userId = null) {
    try {
      const key = this.getKey(name, userId);
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Cache delete error:', error);
      return false;
    }
  }

  /**
   * Clear all cache entries for a specific user
   * @param {string} userId - User ID
   * @returns {boolean} - Success status
   */
  clearUserCache(userId) {
    try {
      if (!userId) {
        console.warn('clearUserCache called without userId');
        return false;
      }

      const keys = Object.keys(localStorage);
      let cleared = 0;
      
      keys.forEach(key => {
        // Only clear entries that match this user's cache pattern
        if (key.startsWith(`${this.prefix}_`) && key.endsWith(`_${userId}`)) {
          try {
            localStorage.removeItem(key);
            cleared++;
          } catch (error) {
            console.error(`Error clearing cache key ${key}:`, error);
          }
        }
      });

      console.log(`Cleared ${cleared} cache entries for user ${userId}`);
      return true;
    } catch (error) {
      console.error('Clear user cache error:', error);
      return false;
    }
  }

  /**
   * Clear all expired entries
   */
  clearExpired() {
    try {
      const keys = Object.keys(localStorage);
      const now = new Date();
      let cleared = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const data = JSON.parse(cached);
              if (data.expiresAt && new Date(data.expiresAt) < now) {
                localStorage.removeItem(key);
                cleared++;
              }
            }
          } catch (error) {
            // Invalid cache entry, remove it
            localStorage.removeItem(key);
            cleared++;
          }
        }
      });

      if (cleared > 0) {
        console.log(`Cleared ${cleared} expired cache entries`);
      }
      return cleared;
    } catch (error) {
      console.error('Clear expired error:', error);
      return 0;
    }
  }

  /**
   * Clear all cache entries (use with caution - typically only on logout)
   * @returns {boolean} - Success status
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      let cleared = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          try {
            localStorage.removeItem(key);
            cleared++;
          } catch (error) {
            console.error(`Error clearing cache key ${key}:`, error);
          }
        }
      });

      console.log(`Cleared ${cleared} cache entries`);
      return true;
    } catch (error) {
      console.error('Clear all error:', error);
      return false;
    }
  }

  /**
   * Validate cache entry belongs to user (security check)
   * @param {string} name - Cache entry name
   * @param {string} userId - User ID to validate against
   * @returns {boolean} - True if valid
   */
  validateCache(name, userId) {
    try {
      if (!userId) return false;
      
      const key = this.getKey(name, userId);
      const cached = localStorage.getItem(key);
      
      if (!cached) return false;
      
      const data = JSON.parse(cached);
      
      // Validate userId matches
      if (data.userId !== userId) {
        console.warn('Cache validation failed - userId mismatch');
        this.delete(name, userId);
        return false;
      }
      
      // Validate not expired
      if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
        this.delete(name, userId);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Cache validation error:', error);
      return false;
    }
  }
}

// Create singleton instance
const cache = new SecureCache();

// Clean up expired entries on initialization
if (typeof window !== 'undefined') {
  cache.clearExpired();
}

export default cache;
export { SecureCache };

